import type { ReactElement } from 'react';
import { FileText, RotateCcw, X } from 'lucide-react';
import { formatByteSize } from '@xdocs/i18n';
import { Button, cn, useI18n } from '@xdocs/ui';
import type { UploadQueueItem, UploadQueueItemStatus } from './useUploadQueue.js';
import { useUploadsT } from './useUploadsT.js';
import styles from './UploadQueueList.module.css';

export interface UploadQueueListProps {
  items: readonly UploadQueueItem[];
  onRetry: (itemId: string) => void;
  onRemove: (itemId: string) => void;
  className?: string | undefined;
}

const STATUS_KEY: Record<UploadQueueItemStatus, string> = {
  validating: 'uploads:status.validating',
  queued: 'uploads:status.queued',
  uploading: 'uploads:status.uploading',
  uploaded: 'uploads:status.uploaded',
  ingesting: 'uploads:status.ingesting',
  ready: 'uploads:status.ready',
  failed: 'uploads:status.failed',
};

/** F18 upload queue: per-file progress bar (upload phase), then a live
 * ingest status (`ingesting`/`ready`/`failed`) once bytes land — E19 states
 * per row (queued/uploading/ready/failed), retry + remove/cancel actions. */
export function UploadQueueList({ items, onRetry, onRemove, className }: UploadQueueListProps): ReactElement {
  const { locale } = useI18n();
  const { t } = useUploadsT();

  if (items.length === 0) {
    return <p className={cn(styles['empty'], className)}>{t('uploads:queue.empty')}</p>;
  }

  return (
    <ul className={cn(styles['list'], className)}>
      {items.map((item) => {
        const isIndeterminate = item.status === 'ingesting';
        const isDone = item.status === 'ready';
        const isFailed = item.status === 'failed';
        return (
          <li key={item.id} className={styles['row']} data-status={item.status}>
            <span className={styles['icon']} aria-hidden="true">
              <FileText size={16} />
            </span>
            <div className={styles['body']}>
              <div className={styles['topLine']}>
                <span className={styles['name']}>{item.file.name}</span>
                <span
                  className={cn(styles['statusLabel'], isFailed && styles['statusError'], isDone && styles['statusSuccess'])}
                  aria-live="polite"
                >
                  {t(STATUS_KEY[item.status] as Parameters<typeof t>[0])}
                </span>
              </div>
              <div className={styles['metaLine']}>
                <span>{formatByteSize(locale, item.file.size)}</span>
              </div>
              {!isFailed && !isDone && (
                <div className={styles['progressTrack']} role="progressbar" aria-valuenow={isIndeterminate ? undefined : item.progress} aria-valuemin={0} aria-valuemax={100}>
                  <div
                    className={cn(styles['progressFill'], isIndeterminate && styles['progressIndeterminate'])}
                    style={isIndeterminate ? undefined : { width: `${item.progress}%` }}
                  />
                </div>
              )}
              {isFailed && item.errorMessage && (
                <p role="alert" className={styles['errorText']}>
                  {item.errorMessage}
                </p>
              )}
            </div>
            <div className={styles['actions']}>
              {isFailed && item.canRetry && (
                <Button size="icon" variant="ghost" aria-label={t('uploads:action.retry')} onClick={() => onRetry(item.id)}>
                  <RotateCcw size={15} aria-hidden="true" />
                </Button>
              )}
              {(item.status === 'queued' || item.status === 'uploading' || isFailed) && (
                <Button size="icon" variant="ghost" aria-label={t('uploads:action.remove')} onClick={() => onRemove(item.id)}>
                  <X size={15} aria-hidden="true" />
                </Button>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
